import { useRef, useState } from "react";
import {
  saveStory,
  saveProject,
  saveVideo,
} from "../../../js/api/member-profile/member-profile";
import { SharedIcon } from "../shared/shared-icon";
import { StoryFields } from "./story-fields";
import { ProjectFields } from "./project-fields";
import { VideoFields } from "./video-fields";

/**
 * The edit form behind My Stories, My Projects and My Videos.
 *
 * One component for all three because everything except the middle differs by nothing: the title,
 * the "Not yet published" state, the Cancel / Save / Save & Publish footer and the way errors come
 * back are identical, and the designs draw them identically.
 *
 * WHAT THIS DOES NOT DO IS VALIDATE. MemberProfileRules on the server decides what a legal item is
 * — the caps, the character limits, both project images, exactly one video source — and answers
 * with errors keyed by property alias. Re-implementing any of that here would create a second
 * home for a rule, and the two would drift the first time one changed. The form's job is to
 * collect, send, and put each message back beside the input it names.
 *
 * COPY ARRIVES AS A PROP. Every string has an English fallback so the component still reads on its
 * own, but ZoneEditor.cshtml passes a `copy` object built from the Umbraco dictionary — the same
 * rule every Razor view here follows. A literal in this file would be a second home for wording an
 * editor is entitled to change.
 */
export function ZoneEditor({
  zone,
  itemKey,
  isPublished,
  listUrl,
  settings,
  values,
  copy,
  initialTitle,
  initialRoomTypeKey,
}) {
  const [form, setForm] = useState(() =>
    initialForm(values, initialTitle, offeredRoomType(settings, initialRoomTypeKey))
  );
  const [errors, setErrors] = useState({});
  const [summary, setSummary] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const titleRef = useRef(null);

  const c = { ...defaultCopy, ...(copy ?? {}) };
  const update = (patch) => setForm((current) => ({ ...current, ...patch }));

  const handleSave = async (publish) => {
    setIsSaving(true);
    setErrors({});
    setSummary(null);

    const result = await save(zone, { ...toRequest(zone, form), itemKey, publish });

    setIsSaving(false);

    if (result.isSuccess) {
      // PUBLISHING IS THE END OF THE JOB, so it goes back to the list — Stories → create → Save &
      // Publish → Stories, where the member can see the thing they just published sitting in it.
      //
      // A plain Save is not the end of the job: the member is still working, and a first save has
      // to move the browser onto the item's own edit URL or the next Save would create a SECOND
      // item rather than update the first. The server decides where that is.
      window.location.assign(publish ? listUrl : result.redirectUrl ?? listUrl);
      return;
    }

    // Field errors are keyed by property alias, which is what the field components look
    // themselves up by. Anything without a field - a refusal, a lapsed session - has no input to
    // sit beside and belongs in the summary.
    setErrors(result.errors ?? {});
    setSummary(result.message ?? summaryFor(result.errors, c));
  };

  // The room a project is tagged with, for the readout under the title.
  const chosenRoomType = settings.roomTypes?.find((roomType) => roomType.key === form.roomTypeKey);

  const fieldProps = { form, update, errors, settings, copy: c };

  return (
    <div className="member-profile-editor" data-testid="zone-editor-form">
      <div className="member-profile-editor__head">
        {/*
          The designs edit the title in place: a large condensed uppercase heading with a pencil
          beside it, not a labelled box above the form.

          It is a REAL INPUT underneath, not a contenteditable. An input is labelled natively,
          honours maxLength, pastes as plain text, and is what page.fill() drives in the E2E
          suite; a contenteditable would need every one of those written by hand, and worse. The
          design's appearance is the requirement — the element carrying it is ours to choose.

          The visually-hidden span is what gives the h1 an accessible name. A heading takes its
          name from its text content and an input's value is not text content, so without it the
          page's only h1 announces as empty.
        */}
        <h1 className="member-profile-editor__title-heading">
          <span className="visually-hidden">{form.title || c.titlePlaceholder}</span>

          <input
            id="zone-editor-title"
            ref={titleRef}
            type="text"
            className="member-profile-editor__title"
            data-testid="zone-editor-title"
            placeholder={c.titlePlaceholder}
            aria-label={c.titleLabel}
            maxLength={settings.titleMaxLength}
            value={form.title}
            onChange={(event) => update({ title: event.target.value })}
          />

          <button
            type="button"
            className="member-profile-editor__title-edit"
            data-testid="zone-editor-title-edit"
            aria-label={c.editTitle}
            onClick={() => titleRef.current?.focus()}
          >
            <SharedIcon iconName="#sprite-edit" aria-hidden="true" />
          </button>
        </h1>

        {errors.Title ? (
          <p className="member-profile-editor__error" role="alert" data-testid="error-title">
            {errors.Title}
          </p>
        ) : null}

        {/* The meta stack under the heading: what this is, and whether anyone else can see it
            yet. The designs put the room type here rather than in the form body. */}
        <div className="member-profile-editor__meta">
          {/* The design shows the chosen room here, under the title. It is a READOUT, not the
              control — the control is a proper labelled Select in the form body, the same one
              CREATE NEW COLLECTION uses. A select dressed as a line of text was easy to mistake
              for decoration. Absent until a room is chosen, because "Select a room" as a meta
              line says nothing. */}
          {zone === "Project" && chosenRoomType ? (
            <p className="member-profile-editor__meta-item">
              <SharedIcon
                className="member-profile-editor__meta-icon"
                iconName="#sprite-icon-home"
                aria-hidden="true"
              />
              {chosenRoomType.name}
            </p>
          ) : null}

          {/* Driven by the item's real published state, which for a new item is simply false. */}
          {!isPublished ? (
            <p className="member-profile-editor__status" data-testid="zone-editor-status">
              <SharedIcon
                className="member-profile-editor__meta-icon"
                iconName="#sprite-private"
                aria-hidden="true"
              />
              {c.notYetPublished}
            </p>
          ) : null}
        </div>

      </div>

      {summary ? (
        <p className="member-profile-editor__summary" role="alert" data-testid="zone-editor-summary">
          {summary}
        </p>
      ) : null}

      <div className="member-profile-editor__fields">
        {zone === "Story" ? <StoryFields {...fieldProps} /> : null}
        {zone === "Project" ? <ProjectFields {...fieldProps} /> : null}
        {zone === "Video" ? <VideoFields {...fieldProps} /> : null}
      </div>

      {/*
        Cancel · Preview · Save · Save & Publish, centred, as every edit frame draws it. Below the
        tablet breakpoint the CSS stacks them full width and moves Cancel last, which is what the
        mobile frames show.

        `.button` and its modifiers come from modules/buttons.scss. These used to carry
        class="cta", which looks like a class and is not one — `.cta` has no standalone rule
        anywhere in the SCSS tree, so all four rendered completely unstyled.
      */}
      <div className="member-profile-editor__footer">
        <a
          className="button transparent member-profile-editor__cancel"
          href={listUrl}
          data-testid="zone-editor-cancel"
        >
          {c.cancel}
        </a>

        {/*
          PREVIEW IS NOT HERE, AND THAT IS DELIBERATE.

          The designs draw a Preview control and this file used to render one, pointing at
          `{listUrl}{itemKey}/?preview=1`. Nothing server-side has ever read that query key:
          MemberProfileZoneController routes the URL straight back to this same edit form, so
          pressing Preview reloaded the editor and threw away everything unsaved along the way.
          Worse than a button that does nothing.

          Building it properly means rendering the DRAFT through the public partials, and those
          partials are typed to the published models — a draft is not in the published cache at
          all. That is a piece of work, not a wiring-up, so the control is gone until it is done
          rather than left in place lying about what it does.
        */}

        <button
          type="button"
          className="button member-profile-editor__save"
          data-testid="zone-editor-save"
          disabled={isSaving}
          onClick={() => handleSave(false)}
        >
          {c.save}
        </button>

        <button
          type="button"
          className="button grey member-profile-editor__publish"
          data-testid="zone-editor-publish"
          disabled={isSaving}
          onClick={() => handleSave(true)}
        >
          {c.saveAndPublish}
        </button>
      </div>

      <p className="member-profile-editor__footer-note">
        <SharedIcon
          className="member-profile-editor__meta-icon"
          iconName="#sprite-alert-circle"
          aria-hidden="true"
        />
        {c.publishNote}
      </p>
    </div>
  );
}

/**
 * English fallbacks. The view overrides every one of these from the Umbraco dictionary; they are
 * here so the component reads and tests on its own, not as the place the wording lives.
 */
const defaultCopy = {
  titleLabel: "Title",
  titlePlaceholder: "Add a title",
  editTitle: "Edit the title",
  notYetPublished: "Not yet published",
  roomTypeLabel: "Room type",
  roomTypePlaceholder: "Select room type",
  cancel: "Cancel",
  save: "Save",
  saveAndPublish: "Save & Publish",
  publishNote: "Publishing will make this public.",
  saveFailed: "Sorry, that could not be saved just now.",
};

const save = (zone, request) => {
  if (zone === "Story") {
    return saveStory(request);
  }

  if (zone === "Project") {
    return saveProject(request);
  }

  return saveVideo(request);
};

/**
 * The form's starting state. Media arrive as objects so a preview can be shown; they go back as
 * bare keys, which is all the server stores.
 */
const initialForm = (values, initialTitle, initialRoomTypeKey) => ({
  // `initialTitle` and `initialRoomTypeKey` are what was chosen in the "Add ..." modal on the list
  // page, carried here on the query string. They only ever apply to a new item; an existing one
  // brings its own.
  title: values?.title ?? initialTitle ?? "",
  bodyHtml: values?.bodyHtml ?? "",
  description: values?.description ?? "",
  caption: values?.caption ?? "",
  roomTypeKey: values?.roomTypeKey ?? initialRoomTypeKey ?? "",
  embedUrl: values?.embedUrl ?? "",
  images: values?.images ?? [],
  // Chosen colour chips arrive resolved — key, name, code and rgb — so the field can paint them
  // without a second round trip. They go back as bare keys, which is all `coloursUsed` stores.
  colours: values?.colours ?? [],
  beforeImage: values?.beforeImage ? [values.beforeImage] : [],
  afterImage: values?.afterImage ? [values.afterImage] : [],
  thumbnail: values?.thumbnail ? [values.thumbnail] : [],
  posterImage: values?.posterImage ? [values.posterImage] : [],
  videoFile: values?.videoFile ? [values.videoFile] : [],
});

/**
 * The room key from the query string, but only if it is one the form offers. A hand-edited URL
 * would otherwise preselect a value the select cannot display; the server refuses it on save
 * either way.
 */
const offeredRoomType = (settings, key) =>
  key && settings.roomTypes?.some((roomType) => roomType.key === key) ? key : null;

const firstKey = (images) => (images.length > 0 ? images[0].key : null);

const toRequest = (zone, form) => {
  if (zone === "Story") {
    return {
      title: form.title,
      bodyHtml: form.bodyHtml,
      imageKeys: form.images.map((image) => image.key),
    };
  }

  if (zone === "Project") {
    return {
      title: form.title,
      // An empty select sends nothing rather than an empty string, which would not parse as a key.
      roomTypeKey: form.roomTypeKey || null,
      description: form.description,
      caption: form.caption,
      beforeImageKey: firstKey(form.beforeImage),
      afterImageKey: firstKey(form.afterImage),
      thumbnailKey: firstKey(form.thumbnail),
      colourChipKeys: form.colours.map((colour) => colour.key),
    };
  }

  return {
    title: form.title,
    // Exactly one of these may be set, and the server refuses both. Sending the URL only when
    // there is no file keeps the form from ever posting a contradiction.
    videoFileKey: firstKey(form.videoFile),
    embedUrl: form.videoFile.length > 0 ? null : form.embedUrl,
    description: form.description,
    posterImageKey: firstKey(form.posterImage),
  };
};

/**
 * Something went wrong but no single field owns it. Falls back to the first field message so the
 * member is never left with a form that simply refused without saying why.
 */
const summaryFor = (errors, copy) => {
  const messages = Object.values(errors ?? {});

  return messages.length > 0 ? messages[0] : copy.saveFailed;
};
